import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  validateRegistrationCode,
  registerUserAction,
} from "@/features/auth/services/authService";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";

export const RegisterPage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState<"code" | "form">("code");
  const [code, setCode] = useState("");
  const [codeData, setCodeData] = useState<any>(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await validateRegistrationCode(code);
      setCodeData(data);
      setStep("form");
    } catch (err: any) {
      const raw = typeof err === "string" ? err : (err.message || "Invalid code");
      setError(t(raw, { defaultValue: raw }));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName.trim()) {
      setError(t("auth.first_name_required", { defaultValue: "First name is required." }));
      return;
    }
    if (formData.password.length < 6) {
      setError(t("common.alert.password_length_error", { defaultValue: "Password must be at least 6 characters." }));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t("common.alert.password_match_error"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await registerUserAction({
        ...formData,
        registrationCode: code,
        ...codeData,
      });
      setSuccess(true);
    } catch (err: any) {
      const raw = typeof err === "string" ? err : (err.message || "Registration failed");
      setError(t(raw, { defaultValue: raw }));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-8 text-center">
        <h2 className="text-xl font-bold text-green-600">
          {t("auth.success_account_created")}
        </h2>
        <Link
          to="/login"
          className="mt-4 inline-block text-indigo-600 hover:text-indigo-700"
        >
          {t("auth.back_to_login")}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 md:p-8">
      <h1 className="text-xl md:text-2xl font-bold text-center mb-2">
        {t("auth.register_title")}
      </h1>
      <p className="text-sm text-gray-500 text-center mb-6">
        {step === "code"
          ? t("auth.register_subtitle_step1")
          : t("auth.register_subtitle_step2")}
      </p>

      {step === "code" ? (
        <form onSubmit={handleVerifyCode} className="space-y-4">
          <Input
            label={t("auth.reg_code_label")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" loading={loading} className="w-full">
            {t("auth.verify_button")}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleRegister} className="space-y-4">
          <Input
            label={t("auth.first_name_label")}
            value={formData.firstName}
            onChange={(e) =>
              setFormData((p) => ({ ...p, firstName: e.target.value }))
            }
          />
          <Input
            label={t("auth.last_name_label")}
            value={formData.lastName}
            onChange={(e) =>
              setFormData((p) => ({ ...p, lastName: e.target.value }))
            }
          />
          <Input
            label={t("auth.email_label")}
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((p) => ({ ...p, email: e.target.value }))
            }
          />
          <Input
            label={t("auth.password_label")}
            type="password"
            value={formData.password}
            onChange={(e) =>
              setFormData((p) => ({ ...p, password: e.target.value }))
            }
          />
          <Input
            label={t("auth.confirm_password_label")}
            type="password"
            value={formData.confirmPassword}
            onChange={(e) =>
              setFormData((p) => ({ ...p, confirmPassword: e.target.value }))
            }
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" loading={loading} className="w-full">
            {t("auth.create_account_button")}
          </Button>
          <button
            type="button"
            onClick={() => setStep("code")}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
          >
            {t("auth.back_to_code")}
          </button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-gray-500">
        {t("auth.already_have_account")}{" "}
        <Link to="/login" className="text-indigo-600 hover:text-indigo-700">
          {t("auth.back_to_login")}
        </Link>
      </p>
    </div>
  );
};
